import axios from "axios";
import { toast } from "react-toastify";
import protectedAPI from "../services/axiosInstance";

const unsupportedLangCodes = new Set(["ks", "sd", "sat"]);
const baseUrl = process.env.REACT_APP_BASE_URL;

export interface AudioState {
  id: string | null;
  isPlaying: boolean;
  isLoading: boolean;
}

export type PlayTTSParams = {
  text?: string;
  id: string;
  task_id?: string;
  lang?: string;
  langCode?: string;
  languageMap?: Record<string, string>;
  audioState: AudioState;
  setAudioState: React.Dispatch<React.SetStateAction<AudioState>>;
  toggleGlobalAudio: (id: string) => void;
  playGlobalAudio: (
    blob: Blob,
    options: {
      id: string;
      onLoading?: () => void;
    }
  ) => Promise<void>;
};

interface FetchAndPlayParams {
  audioUrl: string;
  id: string;
  playGlobalAudio: PlayTTSParams["playGlobalAudio"];
  setAudioState: PlayTTSParams["setAudioState"];
}

const delay = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms));

const resetAudioState = (
  setAudioState: PlayTTSParams["setAudioState"]
) => {
  setAudioState({
    id: null,
    isPlaying: false,
    isLoading: false,
  });
};

const startLoading = (
  id: string,
  setAudioState: PlayTTSParams["setAudioState"]
) => {
  setAudioState({
    id,
    isPlaying: false,
    isLoading: true,
  });
};

const resolveLanguage = (
  lang?: string,
  langCode?: string,
  languageMap?: Record<string, string>
) => {
  if (langCode) return langCode;

  if (lang && languageMap) {
    const found = Object.entries(languageMap).find(
      ([, value]) => value.toLowerCase() === lang.toLowerCase()
    );

    return found?.[0] || "en";
  }

  return "en";
};

const validateLanguage = (
  langCode: string,
  lang?: string
) => {
  if (unsupportedLangCodes.has(langCode)) {
    toast.error(
      lang
        ? `TTS is not supported for ${lang}.`
        : "TTS is not supported."
    );

    return false;
  }

  return true;
};

const fetchAndPlay = async ({
  audioUrl,
  id,
  playGlobalAudio,
  setAudioState,
}: FetchAndPlayParams) => {
  try {
    const response = await axios.get(audioUrl, {
      responseType: "blob",
    });

    await playGlobalAudio(response.data, {
      id,
      onLoading: () =>
        startLoading(id, setAudioState),
    });
  } catch {
    resetAudioState(setAudioState);
    toast.error("Failed to load audio.");
  }
};

interface PollAudioParams {
  api: typeof axios | typeof protectedAPI;
  statusUrl: string;
  taskId: string;
  langCode: string;
  id: string;
  playGlobalAudio: PlayTTSParams["playGlobalAudio"];
  setAudioState: PlayTTSParams["setAudioState"];
  maxPolls?: number;
  interval?: number;
}

const pollAudioStatus = async ({
  api,
  statusUrl,
  taskId,
  langCode,
  id,
  playGlobalAudio,
  setAudioState,
  maxPolls = 25,
  interval = 4000,
}: PollAudioParams) => {
  for (let i = 0; i < maxPolls; i++) {
    await delay(interval);

    try {
      const { data } = await api.get(statusUrl, {
        params: {
          task_id: taskId,
          source_language: langCode,
        },
      });

      if (data.status === "ready") {
        await fetchAndPlay({
          audioUrl: data.audio_url,
          id,
          playGlobalAudio,
          setAudioState,
        });

        return;
      }

      if (data.status === "failed") {
        resetAudioState(setAudioState);
        toast.error("Audio generation failed.");
        return;
      }
    } catch {
      resetAudioState(setAudioState);
      toast.error("Failed to check audio status.");
      return;
    }
  }

  resetAudioState(setAudioState);
  toast.error(
    "Audio generation is taking longer than expected. Please try again."
  );
};

export const playTTS = async ({
  text,
  id,
  lang,
  langCode,
  languageMap,
  audioState,
  setAudioState,
  toggleGlobalAudio,
  playGlobalAudio,
}: PlayTTSParams) => {
  try {
    if (audioState.id === id && audioState.isPlaying) {
      toggleGlobalAudio(id);
      return;
    }

    const resolvedLangCode = resolveLanguage(
      lang,
      langCode,
      languageMap
    );

    if (!validateLanguage(resolvedLangCode, lang)) {
      return;
    }

    if (!text?.trim()) {
      toast.error("No text available for speech.");
      return;
    }

    startLoading(id, setAudioState);

    const response = await protectedAPI.post(
      `${baseUrl}api/generate-tts`,
      {
        text,
        source_language: resolvedLangCode,
      },
      {
        responseType: "blob",
      }
    );

    await playGlobalAudio(response.data, {
      id,
      onLoading: () => startLoading(id, setAudioState),
    });
  } catch (err: any) {
    resetAudioState(setAudioState);

    toast.error(
      err?.response?.data?.msg ||
        "Failed to generate audio."
    );
  }
};

export const playTTSProtected = async ({
  id,
  task_id,
  lang,
  langCode,
  languageMap,
  audioState,
  setAudioState,
  toggleGlobalAudio,
  playGlobalAudio,
}: PlayTTSParams) => {
  try {
    if (audioState.id === id && audioState.isPlaying) {
      toggleGlobalAudio(id);
      return;
    }

    if (!task_id) {
      toast.error("Task ID is missing.");
      return;
    }

    const resolvedLangCode = resolveLanguage(
      lang,
      langCode,
      languageMap
    );

    if (!validateLanguage(resolvedLangCode, lang)) {
      return;
    }

    startLoading(id, setAudioState);

    const { data } = await protectedAPI.post(
      `${baseUrl}api/generate-tts`,
      {
        task_id,
        source_language: resolvedLangCode,
      }
    );

    if (data.status === "ready") {
      await fetchAndPlay({
        audioUrl: data.audio_url,
        id,
        playGlobalAudio,
        setAudioState,
      });

      return;
    }

    if (
      data.status === "queued" ||
      data.status === "processing"
    ) {
      await pollAudioStatus({
        api: protectedAPI,
        statusUrl: `${baseUrl}api/generate-tts-status`,
        taskId: task_id,
        langCode: resolvedLangCode,
        id,
        playGlobalAudio,
        setAudioState,
      });

      return;
    }

    resetAudioState(setAudioState);
    toast.error("Unexpected response from server.");
  } catch (err: any) {
    resetAudioState(setAudioState);

    toast.error(
      err?.response?.data?.msg ||
        "Failed to start audio generation."
    );
  }
};

export const playTTSPublic = async ({
  id,
  text,
  lang,
  langCode,
  languageMap,
  audioState,
  setAudioState,
  toggleGlobalAudio,
  playGlobalAudio,
}: PlayTTSParams) => {
  try {
    if (audioState.id === id && audioState.isPlaying) {
      toggleGlobalAudio(id);
      return;
    }

    const resolvedLangCode = resolveLanguage(
      lang,
      langCode,
      languageMap
    );

    if (!validateLanguage(resolvedLangCode, lang)) {
      return;
    }

    startLoading(id, setAudioState);

    const { data } = await axios.post(
      `${baseUrl}api/public-view-generate-tts`,
      {
        task_id: id,
        source_language: resolvedLangCode,
      }
    );

    if (data.status === "ready") {
      await fetchAndPlay({
        audioUrl: data.audio_url,
        id,
        playGlobalAudio,
        setAudioState,
      });

      return;
    }

    if (data.status === "queued" || data.status === "processing") {
      await pollAudioStatus({
        api: axios,
        statusUrl: `${baseUrl}api/public-view-generate-tts-status`,
        taskId: id,
        langCode: resolvedLangCode,
        id,
        playGlobalAudio,
        setAudioState,
        maxPolls: 30,
        interval: 3000,
      });

      return;
    }

    resetAudioState(setAudioState);
    toast.error("Unexpected response from server.");
  } catch (err: any) {
    resetAudioState(setAudioState);

    const msgCode = err?.response?.data?.msg_code;
    const message =
      err?.response?.data?.msg ||
      "Something went wrong while starting audio generation.";

    switch (msgCode) {
      case 400:
      case 404:
      case 500:
        toast.error(message);
        break;

      default:
        toast.error("An unexpected error occurred. Please try again.");
    }
  }
};
